import * as XLSX from 'xlsx';
import {
  every,
  find,
  flatMap,
  isUndefined,
  map,
  reduce,
  some,
  uniq,
} from 'lodash';

import { Timescale } from '../../../types/models';

import {
  filterSeriesBySelection,
  ITemplateScope,
  sheetNameFor,
  TEMPLATE_INFO_SHEET,
  TEMPLATE_SCOPE_KEYS,
} from './exportInputsAsExcel';

import {
  upTakeEventAssumptionsFormatKey,
  validateUploadedValue,
} from './validateUploadedValue';

import { IInputsResponse, IModifiedRowData } from './index';

export type IUploadedTemplateRow = Record<string, string>;
export type IUploadedTemplateOverrides = Record<string, IUploadedTemplateRow[]>;

export interface IAppliedTemplateOverrides {
  // Full rowData per input, shaped like InputTabPanel's own rowData, ready to seed the AG Grid via sessionStorage
  gridStateByInput: Record<string, Record<string, unknown>[]>;
  modifiedRowData: IModifiedRowData[];
  matchedInputNames: string[];
  unmatchedInputNames: string[];
  // How many rows actually differed from the baseline, per matched input - lets the caller tell
  // "sheet matched but nothing in it differed from the server" apart from a real applied change.
  updatedRowCounts: Record<string, number>;
  // Uploaded cells that failed the same checks Edit Assumptions applies; they are blanked in the grid
  invalidCells: IInvalidUploadedCell[];
}

export interface IInvalidUploadedCell {
  inputName: string;
  column: string;
  value: string;
  reason: string;
  // Dimension values identifying the row, e.g. "Country: France, Brand: X"
  row: string;
}

// Matching has to go through the same sanitizing/truncation the export applies to sheet names
export { sheetNameFor };

// Excel round-trips numbers through its own formatting (e.g. "0.050" -> "0.05"), so a strict string
// compare would flag untouched numeric cells as edited. Numbers compare by value; everything else
// (text, dates-as-typed) still has to match exactly.
const valuesAreEqual = (a: string, b: string): boolean => {
  if (a === b) {
    return true;
  }
  if (a === '' || b === '') {
    return false;
  }
  const numericA = Number(a);
  const numericB = Number(b);
  return (
    !Number.isNaN(numericA) && !Number.isNaN(numericB) && numericA === numericB
  );
};

// Reads the client/model/instance/scenario stamped into the template at download. Returns undefined
// when the file has no (or an unreadable) Template Info sheet, e.g. downloaded before this existed.
export const parseTemplateScope = async (
  file: File
): Promise<ITemplateScope | undefined> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheet = workbook.Sheets[TEMPLATE_INFO_SHEET];
  if (!sheet) {
    return undefined;
  }
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: '',
  });
  const scope: ITemplateScope = {};
  TEMPLATE_SCOPE_KEYS.forEach((key) => {
    const row = find(rows, (r) => `${r.Key ?? ''}`.trim() === key);
    const value = `${row?.Value ?? ''}`.trim();
    if (/^\d+$/.test(value)) {
      scope[key] = Number(value);
    }
  });
  return scope;
};

// Fail closed: every field must match, a missing value on either side counts as a mismatch
export const templateScopeMatches = (
  uploaded: ITemplateScope | undefined,
  current: ITemplateScope
): boolean =>
  uploaded !== undefined &&
  every(
    TEMPLATE_SCOPE_KEYS,
    (key) => uploaded[key] !== undefined && uploaded[key] === current[key]
  );

export const parseTemplateWorkbook = async (
  file: File
): Promise<IUploadedTemplateOverrides> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });

  return reduce(
    workbook.SheetNames.filter((name) => name !== TEMPLATE_INFO_SHEET),
    (acc, sheetName) => {
      const sheet = workbook.Sheets[sheetName];
      // Formatted text (raw: false) is what Excel *displays*, so it can be rounded (0.123456 -> "0.12").
      // The raw read keeps the stored number at full precision; use it for plain numeric cells only,
      // leaving dates / percents / text as their formatted string.
      const formattedRows = XLSX.utils.sheet_to_json<IUploadedTemplateRow>(
        sheet,
        { defval: '', raw: false }
      );
      const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
        defval: '',
        raw: true,
      });
      const rows = map(formattedRows, (formattedRow, rowIndex) =>
        reduce(
          formattedRow,
          (row, formatted, key) => {
            const rawValue = rawRows[rowIndex]?.[key];
            const isPlainNumber =
              typeof rawValue === 'number' &&
              formatted.trim() !== '' &&
              !Number.isNaN(Number(formatted.replace(/,/g, '')));
            return {
              ...row,
              [key]: isPlainNumber ? String(rawValue) : formatted,
            };
          },
          {} as IUploadedTemplateRow
        )
      );
      return { ...acc, [sheetName]: rows };
    },
    {} as IUploadedTemplateOverrides
  );
};

// Narrows each input's Series to the dimension values present in its uploaded sheet, so the grid
// shows only what the file covers. Uses the same `Header>Value` paths as the Download template
// filters; inputs without a sheet, or dimension columns missing from the sheet, aren't filtered.
export const filterInputsToUploadedTemplate = (
  inputs: IInputsResponse[],
  overrides: IUploadedTemplateOverrides
): IInputsResponse[] =>
  map(inputs, (input) => {
    const uploadedRows = overrides[sheetNameFor(input.Name)];
    if (isUndefined(uploadedRows)) {
      return input;
    }
    const sheetPaths = flatMap(input.DimensionHeaders, (header) =>
      some(uploadedRows, (row) =>
        Object.prototype.hasOwnProperty.call(row, header.Name)
      )
        ? uniq(
            map(
              uploadedRows,
              (row) => `${header.Name}>${`${row[header.Name] ?? ''}`.trim()}`
            )
          )
        : []
    );
    return filterSeriesBySelection([input], sheetPaths)[0];
  });

// Merges uploaded sheet rows back onto the Load Selection response. Sheets never carry the row's
// Input_RowID, so rows are matched by their DimensionHeader values instead. The output mirrors what
// InputTabPanel/onCellValueChanged already produce for a manual edit, so the AG Grid (via the
// gridState-inputName-*-modified sessionStorage key it reads on mount) and Publish Changes treat
// uploaded cells exactly like hand-typed ones.
export const applyTemplateOverrides = (
  inputs: IInputsResponse[],
  overrides: IUploadedTemplateOverrides,
  timescale?: Timescale
): IAppliedTemplateOverrides => {
  const gridStateByInput: Record<string, Record<string, unknown>[]> = {};
  const modifiedRowData: IModifiedRowData[] = [];
  const matchedInputNames: string[] = [];
  const updatedRowCounts: Record<string, number> = {};
  const invalidCells: IInvalidUploadedCell[] = [];

  inputs.forEach((input) => {
    const uploadedRows = overrides[sheetNameFor(input.Name)];
    if (isUndefined(uploadedRows)) {
      return;
    }
    matchedInputNames.push(input.Name);

    // Older downloads (from before Group was exported) won't have a "Group" column at all - treat
    // that as "don't use Group to match" rather than requiring every row's Group to equal ''.
    const sheetHasGroupColumn = some(uploadedRows, (row) =>
      Object.prototype.hasOwnProperty.call(row, 'Group')
    );

    const rowData = map(input.Series, (series) => {
      const uploadedRow = find(
        uploadedRows,
        (row) =>
          (!sheetHasGroupColumn ||
            `${row.Group ?? ''}`.trim() ===
              `${(series as any).Group ?? ''}`.trim()) &&
          every(
            input.DimensionHeaders,
            (header) =>
              `${row[header.Name] ?? ''}`.trim() ===
              `${(series as any)[header.Field] ?? ''}`.trim()
          )
      );

      const dimensionFields = reduce(
        input.DimensionHeaders,
        (acc, header) => ({
          ...acc,
          [header.Name]: (series as any)[header.Field],
        }),
        {} as Record<string, string | null>
      );

      const updatedCells: string[] = [];
      const valueFields = reduce(
        input.ValueHeaders,
        (acc, header, index) => {
          const originalValue = `${series.Values[index] ?? ''}`.trim();
          const uploadedRaw = uploadedRow ? uploadedRow[header] : undefined;
          const uploadedValue =
            uploadedRaw !== undefined ? `${uploadedRaw}`.trim() : undefined;
          // A cell present in the upload is applied as-is, blank included (same as clearing it by hand).
          // Only a missing row/column leaves the server value alone.
          const hasUploadedValue = uploadedValue !== undefined;
          let isInvalid = false;
          if (hasUploadedValue && uploadedValue !== '') {
            const formatKey =
              input.TabName === 'Uptake & Event Assumptions'
                ? upTakeEventAssumptionsFormatKey(timescale)[header]
                : input.FormatKey;
            const reason = validateUploadedValue(
              uploadedValue as string,
              formatKey
            );
            if (reason) {
              invalidCells.push({
                inputName: input.Name,
                column: header,
                value: uploadedValue as string,
                reason,
                row: map(
                  input.DimensionHeaders,
                  (dimension) =>
                    `${dimension.Name}: ${dimensionFields[dimension.Name] ?? ''}`
                ).join(', '),
              });
              isInvalid = true;
            }
          }
          // An invalid value is never carried into the grid; the cell is blanked and flagged as edited,
          // the same state as a user clearing it by hand, so it can't be mistaken for the old value
          if (isInvalid) {
            updatedCells.push(header);
            return { ...acc, [header]: '' };
          }
          const resolvedValue = hasUploadedValue
            ? (uploadedValue as string)
            : series.Values[index];
          if (
            hasUploadedValue &&
            !valuesAreEqual(uploadedValue as string, originalValue)
          ) {
            updatedCells.push(header);
          }
          return { ...acc, [header]: resolvedValue };
        },
        {} as Record<string, string>
      );

      return {
        Input_RowID: (series as any).Input_RowID,
        Group: (series as any).Group,
        formatKey: input.FormatKey,
        TabName: input.TabName,
        updatedCells,
        ...dimensionFields,
        ...valueFields,
      };
    });

    gridStateByInput[input.Name] = rowData;
    updatedRowCounts[input.Name] = rowData.filter(
      (row) => row.updatedCells.length > 0
    ).length;

    modifiedRowData.push({
      Input: input.Name,
      Data: map(rowData, (row) => ({
        Input_RowID: row.Input_RowID,
        Group: row.Group,
        Values: map(
          input.ValueHeaders,
          (header) => (row as Record<string, unknown>)[header]
        ),
      })),
      isDirty: some(rowData, (row) => row.updatedCells.length > 0),
      RowIdentifierId: null,
    });
  });

  const selectedSheetNames = map(inputs, (input) => sheetNameFor(input.Name));
  const unmatchedInputNames = Object.keys(overrides).filter(
    (sheetName) => !selectedSheetNames.includes(sheetName)
  );

  return {
    gridStateByInput,
    modifiedRowData,
    matchedInputNames,
    unmatchedInputNames,
    updatedRowCounts,
    invalidCells,
  };
};
